import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppKit } from "@reown/appkit/react";
import { useQueryClient } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { parseUnits } from "viem";
import { useAccount, useSwitchChain, useWalletClient } from "wagmi";

import { ACTIVE_CHAIN } from "@/config/chain";
import { VENUES } from "@/config/contracts";
import { venueAtom } from "@/atoms";
import { CADC } from "@/config/tokens";
import { formatNumber, formatPercent } from "@/lib/format";
import { formatHealthFactor } from "@/lib/loop/risk";
import { useLoopForm, type LoopFormState } from "@/hooks/use-loop-form";
import { useUnwindForm } from "@/hooks/use-unwind-form";
import { useLoopPrerequisites } from "@/hooks/use-loop-prerequisites";
import { submitLoop, submitUnwind, submitUnwindBySwap } from "@/lib/loop/execute";
import { buildPublicClient } from "@/lib/wallet/clients";

const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";

import { ExecuteButton } from "./execute-button";
import { LeverageSlider } from "./leverage-slider";
import { CloseSummary } from "./close-summary";
import { ExistingPosition } from "./existing-position";
import { LoopSummary } from "./loop-summary";
import { MarketReadout } from "./market-readout";
import { ModeToggle, type CardMode } from "./mode-toggle";
import { PrincipalInput } from "./principal-input";
import { RouteDiagram } from "./route-diagram";
import { SetupSteps } from "./setup-steps";
import { SlippageInput } from "./slippage-input";
import { StepFactorInput } from "./step-factor-input";
import { UnwindForm } from "./unwind-form";
import { VenueIconToggle } from "./venue-icon-toggle";

type SubmitState =
  | { kind: "idle" }
  | { kind: "pending" }
  | { kind: "mining"; hash: `0x${string}` }
  | { kind: "success"; hash: `0x${string}` }
  | { kind: "error"; message: string };

const labelFor = (
  connected: boolean,
  wrongChain: boolean,
  isValid: boolean,
  looperDeployed: boolean,
  setupRequired: boolean,
  submit: SubmitState,
  mode: CardMode,
): string => {
  if (!connected) return "Connect wallet to continue";
  if (wrongChain) return "Switch to Base";
  if (submit.kind === "pending") return "Confirm in wallet…";
  if (submit.kind === "mining") return "Mining…";
  if (submit.kind === "success") {
    return mode === "open" ? "Position opened" : "Position closed";
  }
  if (!looperDeployed) return "Looper contract not yet deployed";
  if (!isValid) {
    return mode === "open"
      ? "Enter a principal amount"
      : "Adjust unwind amounts";
  }
  if (setupRequired) return "Complete setup above";
  return mode === "open" ? "Open looped position" : "Close position";
};

// Condensed projection shown in the collapsed builder — the essentials, before
// the user unfolds the full venue/slippage/market breakdown.
const InlineSummary = ({ form }: { form: LoopFormState }) => {
  const { projection, principal, projectedRisk, borrowApr, leverage } = form;
  const netCarry =
    borrowApr == null ? null : -borrowApr * Math.max(0, leverage - 1);
  const rows: ReadonlyArray<{ label: string; value: string; tone?: string }> = [
    { label: "You supply", value: `${formatNumber(principal, 2)} ${CADC.symbol}` },
    {
      label: "Collateral",
      value: `${formatNumber(projection.totalCollateral, 2)} ${CADC.symbol}`,
      tone: "var(--accent-jade)",
    },
    { label: "Borrow", value: `${formatNumber(projection.totalBorrow, 2)} USDC` },
    {
      label: "Net carry",
      value: netCarry == null ? "—" : formatPercent(netCarry, 2),
    },
    {
      label: "Health factor",
      value: projectedRisk ? formatHealthFactor(projectedRisk.healthFactor) : "—",
    },
    {
      label: "Liquidation price",
      value: projectedRisk?.liquidationPrice != null
        ? `${formatNumber(projectedRisk.liquidationPrice, 4)} USDC`
        : "—",
    },
  ];
  return (
    <section>
      <div className="flex items-baseline justify-between mb-4">
        <h3>Projected position</h3>
      </div>
      <ul className="space-y-3">
        {rows.map((r, i) => (
          <li
            key={r.label}
            className="flex items-baseline justify-between gap-6 pb-3"
            style={{
              borderBottom:
                i === rows.length - 1 ? "none" : "1px dashed var(--line-1)",
            }}
          >
            <span
              className="font-sans text-[12px] tracking-[0.04em]"
              style={{ color: "var(--ink-3)" }}
            >
              {r.label}
            </span>
            <span
              className="font-mono tabular text-[14px]"
              style={{ color: r.tone ?? "var(--ink-1)", fontWeight: 400 }}
            >
              {r.value}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
};

// Simple = the abbreviated builder; Advanced = the full venue/slippage/market
// breakdown. A link-style toggle showing the state it switches to.
const DetailToggle = ({
  expanded,
  onChange,
}: {
  expanded: boolean;
  onChange: (e: boolean) => void;
}) => (
  <button
    type="button"
    onClick={() => onChange(!expanded)}
    aria-expanded={expanded}
    className="font-mono text-[10px] tracking-[0.18em] uppercase transition-opacity hover:opacity-70"
    style={{ color: "var(--ink-3)", fontWeight: 500 }}
  >
    {expanded ? "Simple" : "Advanced"}
  </button>
);

type Props = {
  readonly mode: CardMode;
  readonly onModeChange: (m: CardMode) => void;
  readonly expanded: boolean;
  readonly onExpandedChange: (e: boolean) => void;
};

export const LoopCard = ({ mode, onModeChange, expanded, onExpandedChange }: Props) => {
  const [venue, setVenue] = useAtom(venueAtom);
  const { open } = useAppKit();
  const { address: account, chainId, isConnected } = useAccount();
  const { switchChainAsync } = useSwitchChain();
  const walletClient = useWalletClient();
  const form = useLoopForm();
  const unwind = useUnwindForm();
  const queryClient = useQueryClient();
  const [submit, setSubmit] = useState<SubmitState>({ kind: "idle" });

  // Drive both hooks' internal venue state from the lifted prop so the
  // leverage cap and oracle queries stay in sync with the header toggle.
  const setFormVenue = form.setVenue;
  const setUnwindVenue = unwind.setVenue;
  useEffect(() => {
    if (form.venue !== venue) setFormVenue(venue);
  }, [venue, form.venue, setFormVenue]);
  useEffect(() => {
    if (unwind.venue !== venue) setUnwindVenue(venue);
  }, [venue, unwind.venue, setUnwindVenue]);


  const wrongChain = isConnected && chainId !== ACTIVE_CHAIN.id;
  const looper = VENUES[form.venue].looper;
  const looperDeployed = looper !== ZERO_ADDRESS;

  const isMorpho = form.venue === "morpho";

  const principalRaw = useMemo(
    () =>
      form.principal > 0
        ? parseUnits(form.principal.toString(), CADC.decimals)
        : 0n,
    [form.principal],
  );

  // Whether the active venue requires Morpho-style setAuthorization.
  const morphoAuthorizationNeeded = isMorpho;
  const prereqsActive =
    looperDeployed && isConnected && !wrongChain;
  const prereqs = useLoopPrerequisites(account, looper, form.venue, prereqsActive);

  const cadcApproved =
    prereqs.allowanceRaw != null && prereqs.allowanceRaw >= principalRaw;
  const usdcApproved =
    prereqs.usdcAllowanceRaw != null &&
    prereqs.usdcAllowanceRaw >= unwind.maxRepayRaw;
  const isAjna = form.venue === "ajna";
  const morphoAuthSatisfied =
    !morphoAuthorizationNeeded || prereqs.morphoAuthorized;
  const ajnaOperatorSatisfied = !isAjna || !prereqs.ajnaPositionDeployed || prereqs.ajnaOperatorSet;
  const openSetupComplete = cadcApproved && morphoAuthSatisfied && ajnaOperatorSatisfied;
  // `collateral` source spends 0 USDC, so it doesn't need a token approval —
  // just the venue authorization that lets the looper move collateral.
  const closeSetupComplete =
    morphoAuthSatisfied &&
    ajnaOperatorSatisfied &&
    (unwind.source === "collateral" || usdcApproved);

  const formIsValid = mode === "open" ? form.isValid : unwind.isValid;
  const setupComplete =
    mode === "open" ? openSetupComplete : closeSetupComplete;

  const showSetup =
    prereqsActive && formIsValid && !prereqs.isLoading && !setupComplete;
  const setupRequired = looperDeployed && formIsValid && !setupComplete;

  const onExecute = useCallback(async () => {
    if (!isConnected || !account) {
      await open();
      return;
    }
    if (wrongChain) {
      await switchChainAsync({ chainId: ACTIVE_CHAIN.id });
      return;
    }
    if (!looperDeployed) {
      setSubmit({
        kind: "error",
        message: `${form.venue === "morpho" ? "Morpho" : "Ajna"} looper contract is not yet deployed.`,
      });
      return;
    }
    if (!walletClient.data) {
      setSubmit({ kind: "error", message: "Wallet client missing" });
      return;
    }
    setSubmit({ kind: "pending" });
    try {
      const hash =
        mode === "open"
          ? await submitLoop(walletClient.data, {
              account,
              principal: form.principal,
              targetLeverage: form.leverage,
              slippageBps: form.slippageBps,
              venue: form.venue,
              ajnaStepFactorBps: form.ajnaStepFactorBps,
            })
          : unwind.source === "wallet"
            ? await submitUnwind(walletClient.data, {
                account,
                venue: unwind.venue,
                maxRepayRaw: unwind.maxRepayRaw,
                collateralPullRaw: unwind.collateralPullRaw,
              })
            : await submitUnwindBySwap(walletClient.data, {
                account,
                venue: unwind.venue,
                collateralToSwapRaw: unwind.collateralToSwapRaw,
                collateralToReturnRaw: unwind.collateralToReturnRaw,
                slippageBps: unwind.slippageBps,
                repaySharesPart: unwind.repaySharesPart,
                maxRepayNativeRaw: unwind.ajnaMaxRepayNativeRaw,
              });
      setSubmit({ kind: "mining", hash });

      const publicClient = buildPublicClient();
      const receipt = await publicClient.waitForTransactionReceipt({ hash });

      // Anything that read on-chain state for this user/market is now stale.
      // Invalidating these keys triggers an immediate refetch in every hook
      // that's mounted (balances, market, position, prereqs).
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["balance"] }),
        queryClient.invalidateQueries({ queryKey: ["morpho-market"] }),
        queryClient.invalidateQueries({ queryKey: ["morpho-position"] }),
        queryClient.invalidateQueries({ queryKey: ["loop-prereqs"] }),
      ]);

      if (receipt.status === "success") {
        setSubmit({ kind: "success", hash });
        if (mode === "open") {
          form.setPrincipalText("");
        } else {
          unwind.setCollateralText("");
          unwind.setRepayText("");
        }
      } else {
        setSubmit({ kind: "error", message: "Transaction reverted on chain." });
      }
    } catch (e) {
      console.error(e);
      const message = (e as { details?: string; message?: string })?.details ?? (e as { message?: string })?.message ?? "Submission failed";
      setSubmit({ kind: "error", message });
    }
  }, [
    account,
    form,
    isConnected,
    looperDeployed,
    mode,
    open,
    queryClient,
    switchChainAsync,
    walletClient.data,
    wrongChain,
    unwind,
  ]);

  const disabled =
    isConnected &&
    !wrongChain &&
    (!formIsValid ||
      !looperDeployed ||
      setupRequired ||
      submit.kind === "pending" ||
      submit.kind === "mining");

  const label = labelFor(
    isConnected,
    wrongChain,
    formIsValid,
    looperDeployed,
    setupRequired,
    submit,
    mode,
  );

  return (
    <article className="surface-strong rounded-[var(--radius-lg)] overflow-hidden fade-up">
      <header className="px-5 md:px-9 pt-7 md:pt-9 pb-7">
        <div className="flex flex-col items-start gap-5 md:flex-row md:items-end md:justify-between md:gap-6">
          <div className="w-full md:w-auto">
            <div
              className="font-mono text-[10px] tracking-[0.22em] uppercase mb-3"
              style={{ color: "var(--ink-4)" }}
            >
              {mode === "open"
                ? "Strategy · Iterated leverage"
                : "Unwind · Reduce or exit"}
            </div>
            <h2 className="text-[44px] md:text-[56px]" style={{ fontWeight: 350 }}>
              {mode === "open" ? "Loop CADC" : "Close position"}
            </h2>
          </div>
          <div className="flex w-full items-center justify-between gap-3 md:w-auto md:justify-normal md:shrink-0">
            {/* Desktop only: Simple/Advanced sits left of the mode toggle */}
            <div className="hidden md:block">
              <DetailToggle expanded={expanded} onChange={onExpandedChange} />
            </div>
            <ModeToggle value={mode} onChange={onModeChange} />
            {/* Mobile only: market toggle sits right of the mode toggle */}
            <div className="md:hidden">
              <VenueIconToggle value={venue} onChange={setVenue} />
            </div>
          </div>
        </div>
      </header>

      {/* Mobile-only Simple/Advanced toggle — sits above the form inputs.
          Desktop uses the header placement. */}
      <div
        className="md:hidden flex justify-end px-5 py-4"
        style={{ borderTop: "1px solid var(--line-2)" }}
      >
        <DetailToggle expanded={expanded} onChange={onExpandedChange} />
      </div>

      {!expanded ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10 px-5 md:px-9 pb-7 pt-7 md:border-t md:border-[var(--line-2)]">
          {mode === "open" ? (
            <>
              <div className="space-y-9">
                <PrincipalInput
                  value={form.principalText}
                  onChange={form.setPrincipalText}
                  error={form.principalError}
                />
                <LeverageSlider
                  value={form.leverage}
                  min={1}
                  max={form.maxLeverage}
                  step={0.05}
                  onChange={form.setLeverage}
                />
                <RouteDiagram venue={form.venue} proxyAddress={isAjna ? prereqs.ajnaPositionAddress : null} />
              </div>
              <div className="space-y-9">
                <InlineSummary form={form} />
              </div>
            </>
          ) : (
            <>
              <div className="space-y-9">
                <UnwindForm form={unwind} />
                <RouteDiagram venue={unwind.venue} proxyAddress={isAjna ? prereqs.ajnaPositionAddress : null} />
              </div>
              <div className="space-y-9">
                <CloseSummary form={unwind} />
              </div>
            </>
          )}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10 px-5 md:px-9 pb-7 pt-7 md:border-t md:border-[var(--line-2)]">
            <div className="space-y-9">
              {mode === "open" ? (
                <>
                  <PrincipalInput
                    value={form.principalText}
                    onChange={form.setPrincipalText}
                    error={form.principalError}
                  />
                  <LeverageSlider
                    value={form.leverage}
                    min={1}
                    max={form.maxLeverage}
                    step={0.05}
                    onChange={form.setLeverage}
                  />
                  <SlippageInput
                    bps={form.slippageBps}
                    onChange={form.setSlippageBps}
                  />
                  {!isMorpho && (
                    <StepFactorInput
                      bps={form.ajnaStepFactorBps}
                      onChange={form.setAjnaStepFactorBps}
                    />
                  )}
                </>
              ) : (
                <UnwindForm form={unwind} />
              )}

              {/* On wide layouts the route sits at the foot of the left column,
                  directly beneath the inputs. Narrow layouts keep it as a
                  dedicated full-width row below the grid (see below). */}
              <div className="hidden md:block">
                <RouteDiagram
                  venue={mode === "open" ? form.venue : unwind.venue}
                  proxyAddress={isAjna ? prereqs.ajnaPositionAddress : null}
                />
              </div>
            </div>

            <div className="space-y-9">
              {mode === "open" ? (
                <LoopSummary
                  projection={form.projection}
                  principal={form.principal}
                  projectedRisk={form.projectedRisk}
                />
              ) : (
                <CloseSummary form={unwind} />
              )}
              <ExistingPosition
                form={form}
                connected={isConnected}
              />
              <MarketReadout form={form} />
            </div>
          </div>

          <div className="md:hidden px-5 pb-7">
            <RouteDiagram
              venue={mode === "open" ? form.venue : unwind.venue}
              proxyAddress={isAjna ? prereqs.ajnaPositionAddress : null}
            />
          </div>
        </>
      )}

      <footer
        className="px-5 md:px-9 py-7"
        style={{ borderTop: "1px solid var(--line-2)" }}
      >
        {showSetup && account && (
          <SetupSteps
            account={account}
            looper={looper}
            mode={mode}
            tokenApprovalNeeded={mode === "open" || unwind.source === "wallet"}
            tokenApproved={mode === "open" ? cadcApproved : usdcApproved}
            morphoAuthorizationNeeded={morphoAuthorizationNeeded}
            morphoAuthorized={prereqs.morphoAuthorized}
            ajnaOperatorNeeded={isAjna && prereqs.ajnaPositionDeployed && !prereqs.ajnaOperatorSet}
            ajnaOperatorSet={prereqs.ajnaOperatorSet}
            ajnaPositionAddress={prereqs.ajnaPositionAddress}
            onCompleted={prereqs.refetch}
          />
        )}
        <ExecuteButton
          disabled={disabled}
          label={label}
          onClick={onExecute}
        />
        {submit.kind === "error" && (
          <p
            className="mt-4 font-mono text-[11px] tracking-[0.04em]"
            style={{ color: "var(--accent-rose)" }}
          >
            {submit.message}
          </p>
        )}
        {(submit.kind === "mining" || submit.kind === "success") && (
          <p
            className="mt-4 font-mono text-[11px] tabular tracking-[0.04em]"
            style={{
              color:
                submit.kind === "success"
                  ? "var(--accent-jade)"
                  : "var(--ink-3)",
            }}
          >
            <a
              href={`${ACTIVE_CHAIN.blockExplorers?.default.url}/tx/${submit.hash}`}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline"
              style={{ color: "inherit" }}
            >
              tx · {submit.hash.slice(0, 10)}…{submit.hash.slice(-8)}
            </a>
          </p>
        )}
      </footer>
    </article>
  );
};
